import React from 'react';
import type { Editor } from '@tiptap/react';

import { autoConvertImagePlaceholders } from '~/components/document-editor/editor-image-placeholder';
import {
  shouldApplyExternalEditorContent,
  shouldClearAwaitingSaveEcho
} from '~/components/document-editor/editor-content-sync';
import { parseFullHTML } from '~/components/document-editor/template';
import { mergeBodyHTMLForSave } from '~/components/document-editor/editor-document';

const applyBodyToEditor = (
  editor: Editor,
  params: {
    content: string;
    parsed: ReturnType<typeof parseFullHTML>;
    documentPrefixRef: React.MutableRefObject<string | null>;
    documentSuffixRef: React.MutableRefObject<string | null>;
    lastContentRef: React.MutableRefObject<string>;
    lastSavedContentRef: React.MutableRefObject<string>;
  }
) => {
  const {
    content,
    parsed,
    documentPrefixRef,
    documentSuffixRef,
    lastContentRef,
    lastSavedContentRef
  } = params;

  documentPrefixRef.current = parsed?.prefix ?? null;
  documentSuffixRef.current = parsed?.suffix ?? null;
  // Content from outside is where the reader's undo history starts, not a step in it: undoing
  // past it would put back the version Cara or a refit replaced, and autosave would keep that.
  editor
    .chain()
    .setMeta('addToHistory', false)
    .setContent(parsed ? parsed.bodyInner : content, { emitUpdate: false })
    .run();

  autoConvertImagePlaceholders(editor, { skipIfFocused: true });
  // Parsing normalizes whitespace, attributes and table markup. The clean baseline
  // must use that same HTML or a later content update looks like it would erase edits.
  lastContentRef.current = editor.getHTML();
  lastSavedContentRef.current = mergeBodyHTMLForSave(
    lastContentRef.current,
    documentPrefixRef.current,
    documentSuffixRef.current
  );
};

/**
 * Sync TipTap document content with the `content` prop: full-HTML head/body split,
 * artifact switches, and guarded external updates when there are no unsaved edits.
 */
export function useEditorContentSync(params: {
  editor: Editor | null;
  content: string;
  artifactId?: string;
  /** Includes the toolbar, whose controls operate on the editor's current selection. */
  focusScopeRef?: React.RefObject<HTMLElement | null>;
  getContentToSave: (bodyHTML: string) => string;
  documentPrefixRef: React.MutableRefObject<string | null>;
  documentSuffixRef: React.MutableRefObject<string | null>;
  lastContentRef: React.MutableRefObject<string>;
  lastSavedContentRef: React.MutableRefObject<string>;
  isInitialMountRef: React.MutableRefObject<boolean>;
  awaitingSaveEchoRef: React.MutableRefObject<boolean>;
  /**
   * Saves the server has not answered yet. While any is pending, the editor matching the last
   * answered save is not proof it holds nothing unsaved: a queued save would still overwrite
   * whatever is loaded now.
   */
  pendingSavesRef: React.MutableRefObject<number>;
  /** Changes when the pending saves settle, so a document skipped while they were out loads then. */
  savesSettled?: number;
}) {
  const {
    editor,
    content,
    artifactId,
    focusScopeRef,
    getContentToSave,
    documentPrefixRef,
    documentSuffixRef,
    lastContentRef,
    lastSavedContentRef,
    isInitialMountRef,
    awaitingSaveEchoRef,
    pendingSavesRef,
    savesSettled
  } = params;

  const lastArtifactIdRef = React.useRef<string | undefined>(artifactId);

  React.useEffect(() => {
    // A destroyed editor still satisfies the null check but has torn down its
    // command manager, so touching commands throws. React remounts effects
    // with the pre-destroy instance under StrictMode and on keyed remounts.
    if (!editor || editor.isDestroyed) return;

    const parsed = parseFullHTML(content);
    const documentIdentityChanged =
      artifactId !== undefined && artifactId !== lastArtifactIdRef.current;

    if (shouldClearAwaitingSaveEcho(content, lastSavedContentRef.current)) {
      awaitingSaveEchoRef.current = false;
    }

    if (documentIdentityChanged) {
      lastArtifactIdRef.current = artifactId;
      awaitingSaveEchoRef.current = false;
      applyBodyToEditor(editor, {
        content,
        parsed,
        documentPrefixRef,
        documentSuffixRef,
        lastContentRef,
        lastSavedContentRef
      });
      isInitialMountRef.current = false;
      return;
    }

    if (isInitialMountRef.current) {
      applyBodyToEditor(editor, {
        content,
        parsed,
        documentPrefixRef,
        documentSuffixRef,
        lastContentRef,
        lastSavedContentRef
      });
      isInitialMountRef.current = false;
      return;
    }

    const focusScope = focusScopeRef?.current ?? editor.view.dom;
    // Toolbar controls still need the selection after the editor itself blurs.
    // Reconcile only once focus leaves the entire editing panel.
    const syncWhenIdle = () => {
      if (
        editor.isDestroyed ||
        editor.isFocused ||
        editor.view.composing ||
        pendingSavesRef.current > 0 ||
        focusScope.contains(focusScope.ownerDocument.activeElement)
      )
        return;
      const editorBodyHTML = editor.getHTML();
      if (
        !shouldApplyExternalEditorContent({
          incomingContent: content,
          editorBodyHTML,
          lastSavedFullContent: lastSavedContentRef.current,
          awaitingSaveEcho: awaitingSaveEchoRef.current,
          getContentToSave
        })
      ) {
        return;
      }

      applyBodyToEditor(editor, {
        content,
        parsed,
        documentPrefixRef,
        documentSuffixRef,
        lastContentRef,
        lastSavedContentRef
      });
    };
    syncWhenIdle();
    let active = true;
    const onFocusOut = () => {
      // activeElement is transiently the body during blur, before the next control focuses.
      queueMicrotask(() => {
        if (active) syncWhenIdle();
      });
    };
    focusScope.addEventListener('focusout', onFocusOut);
    return () => {
      active = false;
      focusScope.removeEventListener('focusout', onFocusOut);
    };
  }, [
    artifactId,
    awaitingSaveEchoRef,
    content,
    documentPrefixRef,
    documentSuffixRef,
    editor,
    focusScopeRef,
    getContentToSave,
    isInitialMountRef,
    lastContentRef,
    lastSavedContentRef,
    pendingSavesRef,
    savesSettled
  ]);
}
